import type { PDFReader, PDFReaderOptions } from "@pdfking/renderer-api"
import type { PdfiumApi, Pointer } from "@pdfking/renderer-wasm-pdfium"
import { Sleep } from "@pdfking/tfunc"

import { ExtractPage } from "../functions/ExtractPage"
import { GetLastError } from "../functions/GetLastError"
import { ReadDocument } from "../functions/ReadDocument"
import { RenderPage } from "../functions/RenderPage"
import type { InternalPDFReader } from "../types/InternalTypes"

const PDF_DATA_AVAIL = 1

export const CreatePDFReader = async (
  api: PdfiumApi,
  options: PDFReaderOptions
): Promise<PDFReader> => {
  const result = await ReadDocument(api, options)

  const close = async () => {
    if (result.doc_pointer) {
      await api.call("FPDF_CloseDocument", result.doc_pointer)
    }

    for (const func of result.cleanup_functions || []) {
      func()
    }

    for (const key in result.blobs_array) {
      delete result.blobs_array[key]
    }
  }

  const is_page_available = async (page: number) => {
    if (!result.avail) {
      return true
    }

    const status = await api.call(
      "FPDFAvail_IsPageAvail",
      result.avail,
      page - 1,
      result.hints_ptr as Pointer
    )

    return status === PDF_DATA_AVAIL
  }

  const ensure_page = async (page: number, delay = 50) => {
    while (true) {
      if (await is_page_available(page)) {
        return page
      } else {
        const { done } = await result.promise_generator!.next()

        if (done && !(await is_page_available(page))) {
          await close()
          throw new Error(`Page ${page} not available after maximum attempts`)
        }
      }
      await Sleep(delay)
    }
  }

  const error = await GetLastError(api.call)

  if (error) console.error(error.message)

  const internal_page_count = error
    ? 0
    : await api.call("FPDF_GetPageCount", result.doc_pointer)

  const res: InternalPDFReader = {
    doc_pointer: result.doc_pointer,
    close,

    async page_count() {
      return internal_page_count
    },

    async extract_page_as_pdf(index: number) {
      await ensure_page(index)
      return ExtractPage(api, result.doc_pointer, index)
    },

    async rasterize(
      index: number,
      options?: { scale?: number; max_size?: number }
    ) {
      await ensure_page(index)
      return RenderPage(api, result.doc_pointer, index, options)
    },
  }

  return res
}
